/**
 * Captures Meta ad attribution from the landing URL and writes it to cart
 * attributes, so it survives through to the order's note_attributes at checkout.
 *
 * The store splits profit by whose creative drove the sale, so losing this
 * costs real money. It is deliberately theme code — not an app, not a pixel.
 *
 * Rules this implements:
 *
 *   - First touch wins. A later ad click in the same session does not overwrite
 *     the value already recorded.
 *   - It fails silently. Direct checkout may briefly wait for the bounded
 *     capture, but an error or timeout can never prevent a purchase.
 *   - Attribute keys are `_`-prefixed, which hides them from the customer in
 *     cart and checkout while still carrying them to the order.
 *
 * Query parameter names are configurable in Theme settings → Ad attribution,
 * because they have to match whatever the Meta ad URL actually sends.
 */

const SESSION_KEY = 'rovelet:attribution-captured';
const ATTR_AD_ID = '_ad_id';
const ATTR_AD_OWNER = '_ad_owner';

/**
 * Rejects values that are absent, blank, or an unexpanded Meta macro.
 *
 * When a Meta ad URL is misconfigured the `{{ad.id}}` macro is not substituted
 * and arrives as the literal string. Storing that is worse than storing
 * nothing: it looks like real attribution in the order export.
 *
 * @param {string | null} value
 * @returns {string | null}
 */
function clean(value) {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.includes('{{') || trimmed.includes('}}')) return null;

  // Guard the order export against an absurdly long injected value.
  return trimmed.length > 255 ? trimmed.slice(0, 255) : trimmed;
}

/**
 * Reads a route from Horizon's global Theme object, falling back to the
 * unprefixed default. Markets prefixes cart routes per locale (/en-au/cart),
 * so the Theme value is the correct one wherever it exists.
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
function route(key, fallback) {
  const value = globalThis.Theme?.routes?.[key];
  return typeof value === 'string' && value !== '' ? value : fallback;
}

async function captureAttribution(signal) {
  const config = document.querySelector('script[data-ad-attribution]');
  const adParam = config?.dataset.adParam || 'ad_id';
  const ownerParam = config?.dataset.ownerParam || 'owner';

  const params = new URLSearchParams(window.location.search);
  const adId = clean(params.get(adParam));
  const adOwner = clean(params.get(ownerParam));

  // Nothing to record. This is the overwhelmingly common case — every page view
  // that is not an ad landing — so it costs one URL parse and stops here.
  if (!adId && !adOwner) return;

  // First touch within the session. Cheap guard that avoids the network on
  // every subsequent ad-tagged page view.
  if (sessionStorage.getItem(SESSION_KEY)) return;

  // The cart outlives the session, so the session guard alone would let a
  // second session overwrite attribution captured in the first. Check what the
  // cart already holds before writing.
  const cartResponse = await fetch(`${route('cart_url', '/cart')}.js`, {
    headers: { Accept: 'application/json' },
    signal,
  });

  // Without reading the cart, we cannot safely preserve first touch.
  if (!cartResponse.ok) return;
  const cart = await cartResponse.json();
  if (clean(cart?.attributes?.[ATTR_AD_ID]) || clean(cart?.attributes?.[ATTR_AD_OWNER])) {
    // Already attributed. Mark the session so we stop re-checking.
    sessionStorage.setItem(SESSION_KEY, '1');
    return;
  }

  const attributes = {};
  if (adId) attributes[ATTR_AD_ID] = adId;
  if (adOwner) attributes[ATTR_AD_OWNER] = adOwner;

  const updateResponse = await fetch(`${route('cart_update_url', '/cart/update')}.js`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ attributes }),
  });

  // Only claim the session once the write actually landed, so a failed request
  // is retried on the next page view rather than silently dropped.
  if (updateResponse.ok) {
    sessionStorage.setItem(SESSION_KEY, '1');
  }
}

// Checkout can await this same request instead of racing a navigation against
// attribution. Bound the network work and always resolve, including when storage
// is unavailable. Existing Add to cart flows remain independent.
const controller = new AbortController();
const timeout = setTimeout(() => controller.abort(), 1500);
const ready = captureAttribution(controller.signal).catch(() => {}).finally(() => clearTimeout(timeout));
globalThis.RoveletAttribution = { ready };
